/**
 * Progress Service
 * 
 * Handles progress tracking for all hierarchy items:
 * - Dreams, Aspirations, Goals, Tasks, Action Items
 * - Schedule Events, Roadblocks
 */

// Valid item types for progress tracking
export const VALID_ITEM_TYPES = [
  'dream',
  'aspiration', 
  'goal',
  'task',
  'action_item',
  'schedule',
  'roadblock'
] as const

export type ItemType = typeof VALID_ITEM_TYPES[number]

export interface ProgressRecord {
  id: number
  user_id: number
  item_type: ItemType
  item_id: number
  progress_type: string
  progress_value: number
  previous_value: number
  progress_delta: number
  progress_description?: string
  progress_notes?: string
  progress_date: string
  progress_time?: string
  progress_duration?: number
  progress_quality?: 'excellent' | 'good' | 'fair' | 'poor'
  context?: string
  location?: string
  mood?: 'happy' | 'neutral' | 'frustrated' | 'focused' | 'tired'
  energy_level?: 'high' | 'medium' | 'low'
  is_milestone: boolean
  milestone_name?: string
  metrics?: Record<string, any>
  achievements?: string[]
  challenges?: string[]
  created_at: string
  updated_at: string
}

export interface ProgressSummary {
  latest_progress: ProgressRecord | null
  history_count: number
  milestones_count: number
  improvement_trend: 'improving' | 'declining' | 'stable'
  first_record: ProgressRecord | null
  last_record: ProgressRecord | null
}

export interface Milestone {
  id: number
  date: string
  progress_value: number
  improvement: number
  milestone_name?: string
  notes?: string
  is_milestone: boolean
}

export interface ProgressStats {
  total_records: number
  average_progress: number
  progress_by_type: Record<ItemType, {
    count: number
    average: number
    latest: number
  }>
  progress_trend: Array<{
    date: string
    average_progress: number
    total_delta: number
    record_count: number
    milestones: number
  }>
  milestones_achieved: number
  total_time_spent: number
  mood_distribution: Record<string, number>
  energy_distribution: Record<string, number>
}

export interface ProgressDashboard {
  total_items: number
  completed_items: number
  in_progress_items: number
  not_started_items: number
  average_progress: number
  recent_milestones: ProgressRecord[]
  progress_stats: ProgressStats
  item_counts: Record<ItemType | 'schedules' | 'roadblocks', number>
}

export interface UpdateProgressRequest {
  progress_value: number
  notes?: string
  progress_type?: 'update' | 'milestone' | 'checkin' | 'completion'
  duration_minutes?: number
  mood?: 'happy' | 'neutral' | 'frustrated' | 'focused' | 'tired'
  energy_level?: 'high' | 'medium' | 'low'
  context?: string
}

export interface PropagationResult {
  success: boolean
  propagated: boolean
  updated_items: Array<{
    item_type: string
    item_id: number
    item_title: string
    old_progress: number
    new_progress: number
    status?: string
  }>
  total_updated: number
}

export interface UpdateProgressResponse {
  success: boolean
  message: string
  record: ProgressRecord
  is_milestone: boolean
  milestone_name?: string
  propagation?: PropagationResult  // Progress propagation result
}

class ProgressService {
  private baseUrl = '/api/progress'

  /**
   * Get current progress for a specific item
   */
  async getItemProgress(itemType: ItemType, itemId: number): Promise<{
    success: boolean
    item_type: ItemType
    item_id: number
    item_title: string
    current_progress: number
    latest_record: ProgressRecord | null
    summary: ProgressSummary
  }> {
    const response = await fetch(`${this.baseUrl}/item/${itemType}/${itemId}`, {
      credentials: 'include',
      headers: { 'Accept': 'application/json' }
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error || `Failed to get progress for ${itemType} ${itemId}`)
    }

    return response.json()
  }

  /**
   * Update progress for a specific item
   */
  async updateProgress(
    itemType: ItemType, 
    itemId: number, 
    data: UpdateProgressRequest
  ): Promise<UpdateProgressResponse> {
    const response = await fetch(`${this.baseUrl}/item/${itemType}/${itemId}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(data)
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error || `Failed to update progress for ${itemType} ${itemId}`)
    }

    return response.json()
  }

  /**
   * Get progress history for a specific item
   */
  async getItemHistory(itemType: ItemType, itemId: number, days: number = 30): Promise<{
    success: boolean
    history: ProgressRecord[]
    milestones: Milestone[]
    total_records: number
    error?: string
  }> {
    const response = await fetch(
      `${this.baseUrl}/item/${itemType}/${itemId}/history?days=${days}`,
      {
        credentials: 'include',
        headers: { 'Accept': 'application/json' }
      }
    )

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error || 'Failed to get progress history')
    }

    return response.json()
  }

  /**
   * Get all recent milestones across all items
   */
  async getAllMilestones(days: number = 30): Promise<{
    success: boolean
    milestones: ProgressRecord[]
    total: number
  }> {
    const response = await fetch(`${this.baseUrl}/milestones?days=${days}`, {
      credentials: 'include',
      headers: { 'Accept': 'application/json' }
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error || 'Failed to get milestones')
    }

    return response.json()
  }

  /**
   * Get comprehensive progress dashboard data
   */
  async getDashboard(): Promise<{
    success: boolean
  } & ProgressDashboard> {
    const response = await fetch(`${this.baseUrl}/dashboard`, {
      credentials: 'include',
      headers: { 'Accept': 'application/json' }
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error || 'Failed to get progress dashboard')
    }

    return response.json()
  }

  /**
   * Get progress statistics
   */
  async getStats(days: number = 30): Promise<{
    success: boolean
    stats: ProgressStats
  }> {
    const response = await fetch(`/progress/api/stats?days=${days}`, {
      credentials: 'include',
      headers: { 'Accept': 'application/json' }
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error || 'Failed to get progress stats')
    }

    return response.json()
  }

  /**
   * Get progress for multiple items at once
   */
  async getBulkProgress(items: Array<{ item_type: ItemType; item_id: number }>): Promise<{
    success: boolean
    progress: Record<string, number> // key format: "item_type:item_id"
  }> {
    const response = await fetch(`${this.baseUrl}/bulk`, {
      method: 'POST',
      credentials: 'include',
      headers: { 
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ items })
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error || 'Failed to get bulk progress')
    }

    return response.json()
  }

  /**
   * Get list of valid item types
   */
  async getValidTypes(): Promise<{
    success: boolean
    valid_types: ItemType[]
  }> {
    const response = await fetch(`${this.baseUrl}/valid-types`, {
      credentials: 'include',
      headers: { 'Accept': 'application/json' }
    })

    if (!response.ok) {
      return { success: true, valid_types: [...VALID_ITEM_TYPES] }
    }

    return response.json()
  }

  /**
   * Helper: Get milestone celebration message
   */
  getMilestoneCelebration(progress: number): string | null {
    if (progress >= 100) {
      return "🎉 Congratulations! You've completed this item!"
    } else if (progress >= 75) {
      return "💪 Almost there! You're 75% done!"
    } else if (progress >= 50) {
      return "🌟 Halfway there! Keep up the great work!"
    } else if (progress >= 25) {
      return "🚀 Great start! You've made solid progress!"
    }
    return null
  }

  /**
   * Helper: Get progress color class based on value
   */
  getProgressColorClass(progress: number): string {
    if (progress >= 75) return 'bg-ok'
    if (progress >= 50) return 'bg-brand'
    if (progress >= 25) return 'bg-warn'
    return 'bg-soft'
  }

  /**
   * Helper: Get progress text color class based on value
   */
  getProgressTextColorClass(progress: number): string {
    if (progress >= 75) return 'text-ok'
    if (progress >= 50) return 'text-brand'
    if (progress >= 25) return 'text-warn'
    return 'text-muted'
  }

  /**
   * Helper: Get item type display name
   */
  getItemTypeDisplayName(itemType: ItemType): string {
    const displayNames: Record<ItemType, string> = {
      dream: 'Dream',
      aspiration: 'Aspiration',
      goal: 'Goal',
      task: 'Task',
      action_item: 'Action Item',
      schedule: 'Schedule Event',
      roadblock: 'Roadblock'
    }
    return displayNames[itemType] || itemType
  }

  /**
   * Helper: Get item type emoji
   */
  getItemTypeEmoji(itemType: ItemType): string {
    const emojis: Record<ItemType, string> = {
      dream: '🌟',
      aspiration: '🎯',
      goal: '🏆',
      task: '📋',
      action_item: '✅',
      schedule: '📅',
      roadblock: '🚧'
    }
    return emojis[itemType] || '📌'
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // INDIVIDUAL RECORD CRUD OPERATIONS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Get a single progress record by ID
   */
  async getRecord(recordId: number): Promise<{
    success: boolean
    record: ProgressRecord
  }> {
    const response = await fetch(`${this.baseUrl}/records/${recordId}`, {
      credentials: 'include',
      headers: { 'Accept': 'application/json' }
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error || 'Failed to get progress record')
    }

    return response.json()
  }

  /**
   * Update an existing progress record (metadata only, not progress value)
   */
  async updateRecord(recordId: number, data: {
    progress_notes?: string
    progress_description?: string
    progress_quality?: 'excellent' | 'good' | 'fair' | 'poor'
    progress_duration?: number
    mood?: string
    energy_level?: string
    context?: string
    location?: string
  }): Promise<{
    success: boolean
    message: string
    record: ProgressRecord
  }> {
    const response = await fetch(`${this.baseUrl}/records/${recordId}`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(data)
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error || 'Failed to update progress record')
    }

    return response.json()
  }

  /**
   * Delete a progress record
   */
  async deleteRecord(recordId: number): Promise<{
    success: boolean
    message: string
  }> {
    const response = await fetch(`${this.baseUrl}/records/${recordId}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: { 'Accept': 'application/json' }
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error || 'Failed to delete progress record')
    }

    return response.json()
  }

  /**
   * List all progress records for an item with pagination
   */
  async listRecords(itemType: ItemType, itemId: number, options?: {
    page?: number
    per_page?: number
  }): Promise<{
    success: boolean
    item_type: ItemType
    item_id: number
    item_title: string
    records: ProgressRecord[]
    total: number
    page: number
    per_page: number
    has_more: boolean
  }> {
    const params = new URLSearchParams()
    if (options?.page) params.set('page', options.page.toString())
    if (options?.per_page) params.set('per_page', options.per_page.toString())
    
    const url = `${this.baseUrl}/item/${itemType}/${itemId}/records${params.toString() ? '?' + params.toString() : ''}`
    
    const response = await fetch(url, {
      credentials: 'include',
      headers: { 'Accept': 'application/json' }
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error || 'Failed to list progress records')
    }

    return response.json()
  }

  /**
   * Create a new progress record for an item (explicit create method)
   */
  async createRecord(
    itemType: ItemType,
    itemId: number,
    data: UpdateProgressRequest
  ): Promise<UpdateProgressResponse> {
    // Uses the same endpoint as updateProgress
    return this.updateProgress(itemType, itemId, data)
  }

  // ═══════════════════════════════════════════════════════════════════════════════
  // PROGRESS PROPAGATION METHODS
  // ═══════════════════════════════════════════════════════════════════════════════

  /**
   * Get calculated progress for an item based on its children
   */
  async getCalculatedProgress(itemType: ItemType, itemId: number): Promise<{
    success: boolean
    item_type: ItemType
    item_id: number
    item_title: string
    manual_progress: number
    calculated_progress: number
    has_children: boolean
    children_count: number
    children: Array<{
      type: string
      id: number
      title: string
      progress: number
      priority: string
      status: string
    }>
    is_leaf_node: boolean
    progress_source: 'manual' | 'calculated'
  }> {
    const response = await fetch(
      `${this.baseUrl}/item/${itemType}/${itemId}/calculated`,
      {
        credentials: 'include',
        headers: { 'Accept': 'application/json' }
      }
    )

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error || 'Failed to get calculated progress')
    }

    return response.json()
  }

  /**
   * Get the complete progress tree for an item and all its descendants
   */
  async getProgressTree(itemType: ItemType, itemId: number): Promise<{
    success: boolean
    tree: ProgressTreeNode
  }> {
    const response = await fetch(
      `${this.baseUrl}/item/${itemType}/${itemId}/tree`,
      {
        credentials: 'include',
        headers: { 'Accept': 'application/json' }
      }
    )

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error || 'Failed to get progress tree')
    }

    return response.json()
  }

  /**
   * Recalculate progress for all items in the user's hierarchy
   */
  async recalculateAllProgress(): Promise<{
    success: boolean
    updated_items: Array<{
      item_type: string
      item_id: number
      item_title: string
      old_progress: number
      new_progress: number
    }>
    total_updated: number
  }> {
    const response = await fetch(`${this.baseUrl}/recalculate-all`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Accept': 'application/json' }
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error || 'Failed to recalculate progress')
    }

    return response.json()
  }

  /**
   * Manually trigger progress propagation from an item to its ancestors
   */
  async propagateProgress(itemType: ItemType, itemId: number): Promise<{
    success: boolean
    propagated: boolean
    updated_items: Array<{
      item_type: string
      item_id: number
      item_title: string
      old_progress: number
      new_progress: number
      status: string
    }>
    total_updated: number
  }> {
    const response = await fetch(
      `${this.baseUrl}/item/${itemType}/${itemId}/propagate`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Accept': 'application/json' }
      }
    )

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error || 'Failed to propagate progress')
    }

    return response.json()
  }
}

// Progress Tree Node type for the tree structure
export interface ProgressTreeNode {
  item_type: string
  item_id: number
  title: string
  progress: number
  status: string
  priority: string
  depth: number
  children: ProgressTreeNode[]
  calculated_progress?: number
  progress_matches?: boolean
}

// Export singleton instance
export const progressService = new ProgressService()

